'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  MouseEventHandler,
  MouseEvent,
} from 'react'
import TagManager from 'react-gtm-module'
import { throttle } from 'lodash'
import { ASvg } from 'ui/components/atoms/a-svg'
import SvgEvoVisianIclLogo from 'ui/svg/logos/evo-visian-icl/evo-icl-linear-black.svg'
import SvgStaarSurgicalLogo from 'ui/svg/logos/staar-surgical/teal-black.svg'
import SvgVisianIclLogo from 'ui/svg/logos/visian-icl/teal-black.svg'
import classNames from 'classnames'
import './c-the-header.css'
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock'
import * as CONST from 'ui/constants/index'
import { useSiteInfo, isValidHref } from 'shared'
import {
  CBottomFooterCopyright,
  CFooterLinks,
  CLogo,
  HeaderTopNavMenuType,
} from 'tsconfig/craft-types'
import { HeaderContext } from '../../context/header'
import { CTheMenu } from './c-the-menu'
import { CTheNavList } from './c-the-nav-list'
import { CCustomNavList } from './c-custom-nav-list'
import { CNotificationBar, CNotificationBarBlock } from './c-notifications-bar'
import { withDefaultProps } from '../../helpers/with-default-props'

// Safety Information types (only used by doctors app)
type CTopBarItem = {
  showSafetyInformationLink?: boolean
  safetyInformationLinkText?: string
  safetyInformationLinkUrl?: string
}

type Props = {
  isVisible?: boolean
  position?: string
  theme?: string
  isClear?: boolean
  isClearDark?: boolean
  hasProgressBar?: boolean
  countryCode?: string
  brand?: string
  doctorsUrl?: string
  quizUrl?: string
  backendUrl?: string
  baseDomain?: string
  fullDomain?: string
  discoveryUrl?: string
  headerTopNavMenu?: HeaderTopNavMenuType[]
  topFooterLinks?: Array<CFooterLinks>
  bottomFooterLinks?: Array<CFooterLinks>
  isDoctorsSite?: boolean
  logo?: Array<CLogo>
  headerTopBar?: Array<CTopBarItem>
  notificationBar?: Array<CNotificationBarBlock>
}

const defaultProps = {
  isVisible: true,
  position: 'fixed',
  theme: 'light',
  isClear: false,
  isClearDark: false,
  hasProgressBar: false,
  countryCode: '',
  brand: '',
  doctorsUrl: '',
  quizUrl: '',
  backendUrl: '',
  baseDomain: '',
  fullDomain: '',
}

type CustomLinkProps = {
  className?: string
  href?: string
  target?: '_blank' | '_self'
  onClick?: MouseEventHandler
  children?: any
}

export const CustomLink = ({
  className,
  href,
  target = '_self',
  onClick,
  children,
}: CustomLinkProps) => {
  if (!isValidHref(href)) {
    return <></>
  }
  
  if (/^http/.test(href ?? '')) {
    return (
      <a className={className} href={href} target={target} onClick={onClick}>
        {children}
      </a>
    )
  } else {
    return (
      <Link
        className={className}
        href={href ?? '#'}
        target={target}
        onClick={onClick}
        prefetch={false}
      >
        {children}
      </Link>
    )
  }
}

const CTheHeader = (props: Props) => {
  props = withDefaultProps(props, defaultProps)
  const siteInfo = useSiteInfo()
  const { t } = useTranslation('common')
  const headerContext = useContext(HeaderContext)

  const [pageScrollProgress, setPageScrollProgress] = useState<number>(0)
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false)

  const menuRef = useRef()

  const countryFlagCode = useMemo(() => {
    switch (props.countryCode) {
      case 'ar':
        return 'ar'
      case 'es':
        return 'la'
      case 'nl':
        return 'be'
      case 'en':
        return 'ea'
      case 'zh-Hans':
        return 'us'
      default:
        return props.countryCode?.slice(3) ?? ''
    }
  }, [props.countryCode])

  const countryCode = useMemo(() => {
    switch (props.countryCode) {
      case 'ar':
        return 'ar'
      case 'es':
        return 'la'
      case 'nl':
        return 'be'
      case 'en':
        return 'ea'
      case 'zh-Hans':
        return 'us'
      case 'en-GB':
        return 'UK'
      default:
        return props.countryCode?.slice(3) ?? ''
    }
  }, [props.countryCode])

  const handleNavigationLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'headerClicks',
        event_category: 'Header Clicks',
        event_action: event.currentTarget.innerText,
        event_label: event.currentTarget.className,
      },
    })
  }

  const handleLocaleButtonClick = () => {
    headerContext?.setShowLocaleSwitcher(true)
  }

  const handleWindowScroll = () => {
    setPageScrollProgress(
      window.pageYOffset / (document.body.scrollHeight - window.innerHeight)
    )
  }

  const openMenu = () => {
    setIsOpenMenu(true)
    if (menuRef.current) {
      disableBodyScroll(menuRef.current)
    }
  }

  const closeMenu = () => {
    setIsOpenMenu(false)
    if (menuRef.current) {
      enableBodyScroll(menuRef.current)
    }
  }

  const handleWindowScrollThrottled = throttle(handleWindowScroll, 100)

  useEffect(() => {
    window.addEventListener('scroll', handleWindowScrollThrottled)

    return () => {
      window.removeEventListener('scroll', handleWindowScrollThrottled)
    }
  }, [])

  return (
    <>
      <CNotificationBar field={props.notificationBar} />
      <header
        className={classNames(
          'c-the-header top-0 z-50 w-full inset-inline-0',
          props.position,
          {
            'bg-neutral-white text-neutral-dark-gray': true,
          }
        )}
      >
        <div className="bg-[#242424] text-[#817c7d] h-[34px] items-center justify-end hidden px-5 md:flex xl:px-10">
          {['en-US', 'zh-Hans'].includes(props.countryCode ?? '') && (
            <>
              <CustomLink
                className={classNames(
                  'font-bold tracking-wider uppercase transition duration-300 text-p-sm',
                  props.countryCode === 'zh-Hans'
                    ? 'text-neutral-gray'
                    : 'hover:text-neutral-gray'
                )}
                href={`https://sc.${props.baseDomain}`}
              >
                简体中文
              </CustomLink>
              <div className="mx-4 bg-[#817c7d] w-[1px] h-[18px]" />
              <CustomLink
                className={classNames(
                  'font-bold tracking-wider uppercase transition duration-300 text-p-sm',
                  props.countryCode === 'en-US'
                    ? 'text-neutral-gray'
                    : 'hover:text-neutral-gray'
                )}
                href={`https://us.${props.baseDomain}`}
              >
                English
              </CustomLink>
              <div className="mx-4 bg-[#817c7d] w-[1px] h-[18px]" />
            </>
          )}
          {(() => {
            const safetyInfo = props.headerTopBar?.[0];
            // Hide safety info for Brazilian Portuguese and Spanish sites
            if (siteInfo?.iso === 'pt-BR' || siteInfo.isES) return null;
            if (safetyInfo?.showSafetyInformationLink === false) return null;
            const linkText =
              safetyInfo?.safetyInformationLinkText?.trim() ||
              t('titles.safety-information');
            const pathSegment = t('paths.safety-information');
            const defaultUrl = props.discoveryUrl
              ? `${props.discoveryUrl}${pathSegment}`
              : undefined;
            const linkUrl =
              safetyInfo?.safetyInformationLinkUrl?.trim() ||
              defaultUrl;
            if (!linkText || !linkUrl)
              return null;
            return (
              <>
                <CustomLink
                  className="font-bold tracking-wider uppercase transition duration-300 text-p-sm hover:text-neutral-gray"
                  href={linkUrl}
                >
                  {linkText}
                </CustomLink>
                <div className="mx-4 bg-[#817c7d] w-[1px] h-[18px]" />
              </>
            );
          })()}
          {siteInfo.isES ? (
            <div className="flex items-center font-bold tracking-wider uppercase text-p-sm">
              <Image
                className="mx-2"
                src={`${
                  props.backendUrl
                }img/flags/${countryFlagCode.toLowerCase()}.png`}
                aria-hidden="true"
                alt={countryCode}
                width={23}
                height={12}
              />
            </div>
          ) : (
            <button
              title="Change region"
              className="flex items-center font-bold tracking-wider uppercase transition duration-300 text-p-sm hover:text-neutral-gray"
              onClick={handleLocaleButtonClick}
            >
              <Image
                className="mx-2"
                src={`${
                  props.backendUrl
                }img/flags/${countryFlagCode.toLowerCase()}.png`}
                aria-hidden="true"
                alt={countryCode}
                width={23}
                height={12}
              />
              <span className="uppercase">{countryCode}</span>
            </button>
          )}
        </div>
        <div className="c-the-header__main flex items-center justify-between px-5 xl:px-10 h-[72px]">
          {siteInfo.isES ? (
            <CustomLink
              className="inline-block font-bold tracking-wider uppercase text-neutral-dark-gray hover:text-primary-teal transition duration-300"
              href={props.discoveryUrl}
            >
              Home
            </CustomLink>
          ) : props.logo?.length ? (
            <CustomLink
              className="inline-block relative w-[160px] h-[32px]"
              href={
                props?.fullDomain?.includes('doctors') ||
                props?.fullDomain?.includes('quiz')
                  ? `https://${countryCode}.discovericl.com`
                  : props?.fullDomain
              }
            >
              <Image
                src={props.logo?.[0].url || ''}
                priority
                alt={'EVO Visian logo'}
                fill
                style={{
                  objectFit: 'cover',
                }}
              />
            </CustomLink>
          ) : (
            <CustomLink
              className="inline-block"
              href={
                props?.fullDomain?.includes('doctors') ||
                props?.fullDomain?.includes('quiz')
                  ? `https://${countryCode}.discovericl.com`
                  : props?.fullDomain
              }
            >
              {props.brand === CONST.BRANDED.VISIAN_ICL && (
                <ASvg
                  className={classNames('w-[160px] h-[32px]', {
                    'fill-current text-neutral-white c-the-header__logo-graphic':
                      props.isClear || (!props.isClear && props.theme === 'dark'),
                    'fill-current text-neutral-dark-gray c-the-header__logo-graphic':
                      !props.isClear && props.theme === 'light',
                  })}
                  svg={SvgVisianIclLogo}
                  ariaLabel={`Visian ICL ${t('logo')}`}
                />
              )}
              {props.brand === CONST.BRANDED.STAAR_SURGICAL && (
                <ASvg
                  className={classNames('w-[160px] h-[32px]', {
                    'fill-current text-neutral-white c-the-header__logo-graphic':
                      props.isClear || (!props.isClear && props.theme === 'dark'),
                    'fill-current text-neutral-dark-gray c-the-header__logo-graphic':
                      !props.isClear && props.theme === 'light',
                  })}
                  svg={SvgStaarSurgicalLogo}
                  ariaLabel={`Staar Surgical ${t('logo')}`}
                />
              )}
              {props.brand === CONST.BRANDED.EVO_VISIAN_ICL && (
                <ASvg
                  className={classNames('w-[100px] md:w-[160px] h-[32px]', {
                    'fill-current text-neutral-white':
                      props.isClear || (!props.isClear && props.theme === 'dark'),
                    'fill-current text-neutral-dark-gray':
                      !props.isClear && props.theme === 'light',
                  })}
                  svg={SvgEvoVisianIclLogo}
                  ariaLabel={`EVO Visian ${t('logo')}`}
                />
              )}
            </CustomLink>
          )}
          <nav
            aria-label={`${t('header')} ${t('navigation')}`}
            className={`${
              props.doctorsUrl && !siteInfo.isSE ? 'w-[66%]' : ''
            } md:w-auto`}
          >
            {props?.headerTopNavMenu && props.headerTopNavMenu.length > 0 ? (
              <CCustomNavList
                headerTopNavMenu={props.headerTopNavMenu}
                theme={'light'}
                isClear={false}
                isClearDark={props.isClearDark}
                doctorsUrl={props.doctorsUrl}
                quizUrl={props.quizUrl}
                discoveryUrl={props.discoveryUrl}
                openMenu={openMenu}
                handleNavigationLinkClick={handleNavigationLinkClick}
              />
            ) : (
              <CTheNavList
                theme={'light'}
                isClear={false}
                isClearDark={props.isClearDark}
                doctorsUrl={props.doctorsUrl}
                quizUrl={props.quizUrl}
                discoveryUrl={props.discoveryUrl}
                openMenu={openMenu}
                handleNavigationLinkClick={handleNavigationLinkClick}
              />
            )}
          </nav>
        </div>
        <CTheMenu
          menuRef={menuRef}
          isOpen={isOpenMenu}
          brand={props.brand}
          iclBaseUrl={props.backendUrl}
          doctorsUrl={props.doctorsUrl}
          discoveryUrl={props.discoveryUrl}
          quizUrl={props.quizUrl}
          onClose={closeMenu}
          onOpenLocaleSwitcher={handleLocaleButtonClick}
          fullDomain={props.fullDomain}
          headerTopNavMenu={props.headerTopNavMenu}
          topFooterLinks={props.topFooterLinks}
          bottomFooterLinks={props.bottomFooterLinks}
          logo={props.logo}
          headerTopBar={props.headerTopBar}
        />
        {props.hasProgressBar && (
          <div className="w-full c-the-header__progress-bar" aria-hidden="true">
            <div
              className="w-full h-full c-the-header__progress-bar-bar bg-primary-teal"
              style={{
                transform: `scaleX(${pageScrollProgress})`,
              }}
            />
          </div>
        )}
      </header>
    </>
  )
}

CTheHeader.defaultProps = defaultProps

export { CTheHeader }
